/**
 * Persistent left navigation rail — the studio's primary way to move
 * between tools (it replaces the old checklist home view).
 *
 * Layout role: the leftmost column of the workspace row
 * (sidebar → script view → feature panel → timeline dock at the bottom).
 *
 * Each creative tool is one editorial row. The active tool is bold; a
 * tool that already has content shows a trailing dot (•), derived from
 * the same `computeStepStatus` the rest of the studio uses so the dots
 * never disagree with reality.
 *
 * Two "separable" surfaces — the Script column and the Timeline dock —
 * are exposed as toggles in a footer group, since the user can hide
 * either when they're not needed.
 */

import {
  PANEL_REGISTRY,
  type PanelManager,
  type StudioPanelId,
} from "./PanelManager";
import type { ScriptDock } from "./ScriptDock";
import { computeStepStatus, STATUS_STORE_KEYS } from "./studioStatus";
import type { StudioStore } from "./StudioStore";
import type { Timeline } from "./Timeline";
import "./styles/sidebar.css";

interface SidebarItem {
  id: StudioPanelId;
  label: string;
  icon: SidebarIcon;
}

type SidebarIcon =
  | "location"
  | "characters"
  | "assets"
  | "storyboard"
  | "moodboard"
  | "final-video"
  | "script"
  | "timeline";

/**
 * The creative tools, in working order. Script (its own column) and the
 * Timeline (the bottom dock) are intentionally absent — they live as
 * footer toggles below. Labels are short by design (matches the rail
 * mockup); the full titles still live in PANEL_REGISTRY for headers.
 */
const SIDEBAR_ITEMS: SidebarItem[] = [
  { id: "location", label: "Locations", icon: "location" },
  { id: "characters", label: "Characters", icon: "characters" },
  { id: "assets", label: "Assets", icon: "assets" },
  { id: "storyboard", label: "Storyboard", icon: "storyboard" },
  { id: "moodboard", label: "Moodboard", icon: "moodboard" },
  { id: "final_video", label: "Final cut", icon: "final-video" },
];

const ICON_PATHS: Record<SidebarIcon, string> = {
  location:
    '<path d="M12 21s6-5.1 6-11a6 6 0 1 0-12 0c0 5.9 6 11 6 11Z"/><circle cx="12" cy="10" r="2.25"/>',
  characters:
    '<circle cx="9" cy="8" r="3"/><path d="M3.5 19c.4-4 2.2-6 5.5-6s5.1 2 5.5 6"/><path d="M15 5.5a3 3 0 0 1 0 5.8M15.5 13.2c3 .3 4.7 2.2 5 5.3"/>',
  assets:
    '<path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z"/><path d="m4.4 7.7 7.6 4.4 7.6-4.4M12 12v9"/>',
  storyboard:
    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M9 5v14M15 5v14M3 10h6M15 14h6"/>',
  moodboard:
    '<rect x="3" y="4" width="8" height="7" rx="1"/><rect x="13" y="4" width="8" height="11" rx="1"/><rect x="3" y="13" width="8" height="7" rx="1"/><path d="m15.5 18 1-2 1 2 2 .8-2 .8-1 2-1-2-2-.8 2-.8Z"/>',
  "final-video":
    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m10 9 5 3-5 3V9ZM7 5v14M17 5v14"/>',
  script:
    '<path d="M6 3h8l4 4v14H6V3Z"/><path d="M14 3v5h4M9 12h6M9 16h6"/>',
  timeline:
    '<path d="M4 6h16M4 12h16M4 18h16"/><rect x="7" y="4" width="5" height="4" rx="1"/><rect x="13" y="10" width="6" height="4" rx="1"/><rect x="5" y="16" width="8" height="4" rx="1"/>',
};

interface SidebarOptions {
  store: StudioStore;
  panelManager: PanelManager;
  scriptDock: ScriptDock;
  timeline: Timeline;
}

export class Sidebar {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private readonly scriptDock: ScriptDock;
  private readonly timeline: Timeline;

  private readonly root: HTMLElement;
  private readonly itemButtons = new Map<StudioPanelId, HTMLButtonElement>();
  private scriptToggle!: HTMLButtonElement;
  private timelineToggle!: HTMLButtonElement;

  private unsubscribers: (() => void)[] = [];

  constructor(options: SidebarOptions) {
    this.store = options.store;
    this.panelManager = options.panelManager;
    this.scriptDock = options.scriptDock;
    this.timeline = options.timeline;

    this.root = document.createElement("nav");
    this.root.className = "studio-sidebar";
    this.root.setAttribute("aria-label", "Tools");

    const eyebrow = document.createElement("span");
    eyebrow.className = "studio-sidebar-eyebrow";
    eyebrow.textContent = "tools";
    this.root.appendChild(eyebrow);

    const list = document.createElement("div");
    list.className = "studio-sidebar-list";
    for (const item of SIDEBAR_ITEMS) {
      list.appendChild(this.buildItem(item));
    }
    this.root.appendChild(list);

    this.root.appendChild(this.buildFooter());
  }

  /** The element to mount into the workspace row. */
  getElement(): HTMLElement {
    return this.root;
  }

  /** Wire live updates. Call once after mounting. */
  start(): void {
    // Active-tool highlight follows the panel manager.
    this.unsubscribers.push(
      this.panelManager.subscribe((next) => this.syncActive(next)),
    );
    const current = this.panelManager.getCurrentPanelId();
    if (current) this.syncActive(current);

    // Progress dots follow the store.
    for (const key of STATUS_STORE_KEYS) {
      this.unsubscribers.push(this.store.subscribe(key, () => this.syncDots()));
    }
    this.syncDots();
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }

  // ------------------------------------------------------------------
  // Build
  // ------------------------------------------------------------------

  private buildItem(item: SidebarItem): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "studio-sidebar-item";
    button.dataset.tool = item.id;
    button.title = PANEL_REGISTRY[item.id].description;

    button.appendChild(buildIcon(item.icon));

    const label = document.createElement("span");
    label.className = "studio-sidebar-item-label";
    label.textContent = item.label;

    const dot = document.createElement("span");
    dot.className = "studio-sidebar-item-dot";
    dot.setAttribute("aria-hidden", "true");

    button.appendChild(label);
    button.appendChild(dot);
    button.addEventListener("click", (event) => {
      this.scriptDock.restoreSplit();
      this.panelManager.open(item.id);
      if (event.detail > 0) button.blur();
    });

    this.itemButtons.set(item.id, button);
    return button;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement("div");
    footer.className = "studio-sidebar-footer";

    const label = document.createElement("span");
    label.className = "studio-sidebar-footer-label";
    label.textContent = "views";
    footer.appendChild(label);

    this.scriptToggle = this.buildToggle("Script", "script", "script", () => {
      this.scriptDock.toggle();
    });
    this.syncToggle(this.scriptToggle, this.scriptDock.isOpen());

    this.timelineToggle = this.buildToggle("Timeline", "timeline", "timeline", () => {
      this.timeline.toggle();
    });
    this.syncToggle(this.timelineToggle, this.timeline.isExpanded());

    footer.appendChild(this.scriptToggle);
    footer.appendChild(this.timelineToggle);
    return footer;
  }

  private buildToggle(
    label: string,
    key: string,
    icon: SidebarIcon,
    onClick: () => void,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "studio-sidebar-toggle";
    button.dataset.toggle = key;
    button.title = `${label} view`;
    button.setAttribute("aria-label", `${label} view`);

    button.appendChild(buildIcon(icon));

    const text = document.createElement("span");
    text.className = "studio-sidebar-toggle-label";
    text.textContent = label;

    const state = document.createElement("span");
    state.className = "studio-sidebar-toggle-state";
    state.setAttribute("aria-hidden", "true");

    button.appendChild(text);
    button.appendChild(state);
    button.addEventListener("click", onClick);
    return button;
  }

  // ------------------------------------------------------------------
  // Sync (called by subscriptions + the dock/timeline state callbacks)
  // ------------------------------------------------------------------

  private syncActive(activePanelId: StudioPanelId): void {
    for (const [id, button] of this.itemButtons) {
      const active = id === activePanelId;
      button.classList.toggle("is-active", active);
      if (active) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
    }
  }

  private syncDots(): void {
    for (const [id, button] of this.itemButtons) {
      const status = computeStepStatus(this.store, id);
      button.dataset.status = status;
      button.classList.toggle("has-content", status !== "empty");
    }
  }

  /** Reflect the Script column's open state in its sidebar toggle. */
  setScriptOpen(open: boolean): void {
    this.syncToggle(this.scriptToggle, open);
  }

  /** Reflect the Timeline dock's expanded state in its sidebar toggle. */
  setTimelineExpanded(expanded: boolean): void {
    this.syncToggle(this.timelineToggle, expanded);
  }

  private syncToggle(button: HTMLButtonElement, on: boolean): void {
    button.classList.toggle("is-on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
  }
}

function buildIcon(icon: SidebarIcon): HTMLSpanElement {
  const wrapper = document.createElement("span");
  wrapper.className = "studio-sidebar-icon";
  wrapper.setAttribute("aria-hidden", "true");
  wrapper.innerHTML = `<svg viewBox="0 0 24 24" focusable="false">${ICON_PATHS[icon]}</svg>`;
  return wrapper;
}
